import { Await, useLoaderData } from 'react-router-typesafe'
import { confirm } from 'devextreme/ui/dialog'
import { Form as DXForm, FormRef, GroupItem, RequiredRule, SimpleItem } from 'devextreme-react/form'
import { Suspense, useCallback, useRef, useState } from 'react'
import { ArraySourceComposer } from '@/auth/api/connector'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { sede } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useNavigate, useParams } from 'react-router-dom'
import {
  clientMigrationRouteLoader,
  NuovaSede,
  azioniMigrazione,
  AzioneMigrazione,
} from '@/routes/clients/ClientMigration.route'
import { ValueChangedEvent } from 'devextreme/ui/lookup'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { Button, CheckBox, ResponsiveBox, SelectBox, TextBox, Toolbar } from 'devextreme-react'
import { Item as ToolbarItem } from 'devextreme-react/toolbar'
import { Row, Col, Item as ResponsiveItem, Location } from 'devextreme-react/responsive-box'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'
import { AxiosResponse } from 'axios'
import './ClientMigration.scss'

const ClientMigration = () => {
  const { title, getAziende, migrationData } = useLoaderData<typeof clientMigrationRouteLoader>()
  const { clientId } = useParams()
  const [loadPanelVisible, setLoadPanleVisible] = useState<boolean>(false)
  const { service, client } = useQsAdminApiManager()
  const [sediDaMigrareList, setSediDaMigrareList] = useState<NuovaSede[]>(migrationData.sedi)
  const [sediDestinazioneList, setSediDestinazioneList] = useState<sede[]>([])
  const navigate = useNavigate()

  const formRef = useRef<FormRef>(null)

  const getCompanySites = useCallback(
    async (companyId: number) => {
      const sitesData = await service.sede().query((builder, sede) => {
        builder.filter(sede.azienda.props.id.eq(companyId))
        builder.orderBy(sede.principale.desc())
        builder.select('id', 'nome')
      })
      return sitesData.data.value
    },
    [service],
  )

  const updateSede = useCallback((index: number, updates: Partial<NuovaSede>) => {
    setSediDaMigrareList((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], ...updates }
      return updated
    })
  }, [])

  const rowsCount = Math.ceil((sediDaMigrareList.length || 0) / 2)
  const rows = Array.from({ length: rowsCount }, (_, i) => <Row key={i} />)
  const cols = [<Col key={0} />, <Col key={1} />]

  return (
    <>
      <Suspense fallback={<LoadingPanel />}>
        <Await resolve={getAziende}>
          {(aziende) => (
            <>
              <h2 className={clientId ? '' : 'content-block'}>{title}</h2>
              <div
                id="clients-migration-container"
                className={clientId ? 'dx-card responsive-paddings' : 'content-block dx-card responsive-paddings'}
              >
                <LoadingPanel visible={loadPanelVisible}></LoadingPanel>
                <DXForm
                  id={'clients_migration_form'}
                  formData={migrationData}
                  ref={formRef}
                  onFieldDataChanged={(e: FieldDataChangedEvent) => {
                    console.log('formData', migrationData)
                  }}
                  showValidationSummary={true}
                >
                  <GroupItem colCount={1} colSpan={1}>
                    <SimpleItem
                      dataField="azienda_da_migrare"
                      editorType="dxLookup"
                      label={{ text: 'Azienda da migrare' }}
                      editorOptions={{
                        dataSource: ArraySourceComposer('id', aziende.data.value),
                        displayExpr: 'nome',
                        placeholder: `Seleziona l'azienda da migrare...`,
                        showClearButton: true,
                        searchEnabled: true,
                        dropDownOptions: {
                          showTitle: true,
                          title: 'Selezionare azienda da migrare',
                          hideOnOutsideClick: true,
                        },
                        readOnly: true,
                        onValueChanged: (e: ValueChangedEvent) => {
                          if (e.previousValue?.id === e.value?.id) return
                          if (e.value)
                            getCompanySites(e.value.id)
                              .then((sedi: sede[]) => {
                                const newSites: NuovaSede[] = sedi.map((sede: sede) => ({
                                  id: sede.id,
                                  principale: null,
                                  nome: sede.nome,
                                  note: null,
                                  email: null,
                                  email_pec: null,
                                  telefono: null,
                                  fax: null,
                                  leg_srvpath: null,
                                  leg_email_rin_ad: null,
                                  leg_rif_ad: null,
                                  strada: null,
                                  citta: null,
                                  provincia: null,
                                  cap: null,
                                  stato: null,
                                  archived: null,
                                  nuovo_nome: `${sede.nome}`,
                                  selected: true,
                                  azione: 'nuova' as AzioneMigrazione,
                                  geocode_source: null,
                                  geocode_address_hash: null,
                                  geocode_status: null,
                                  geocode_updated_at: null,
                                  latitude: null,
                                  longitude: null,
                                }))
                                setSediDaMigrareList(newSites)
                              })
                              .catch((error) => {
                                console.log('error', error)
                              })
                        },
                      }}
                    >
                      <RequiredRule message="Azienda da migrare obbligatoria" />
                    </SimpleItem>
                    <SimpleItem
                      dataField="azienda_destinazione"
                      editorType="dxLookup"
                      label={{ text: 'Azienda destinazione' }}
                      editorOptions={{
                        dataSource: ArraySourceComposer('id', aziende.data.value),
                        displayExpr: 'nome',
                        placeholder: `Seleziona l'azienda destinazione...`,
                        searchEnabled: true,
                        dropDownOptions: {
                          showTitle: true,
                          title: 'Selezionare azienda destinazione',
                          hideOnOutsideClick: true,
                        },
                        onValueChanged: (e: ValueChangedEvent) => {
                          if (e.previousValue?.id === e.value?.id) return
                          if (e.value)
                            getCompanySites(e.value.id)
                              .then((sedi: sede[]) => {
                                setSediDestinazioneList(sedi)
                                setSediDaMigrareList((prev) =>
                                  prev.map((nuova_sede) => ({
                                    ...nuova_sede,
                                    sede_destinazione: undefined,
                                    azione: 'nuova' as AzioneMigrazione,
                                    nuovo_nome: sedi.find((sede) => nuova_sede.nome === sede.nome)
                                      ? nuova_sede.nome + ' 1'
                                      : `${nuova_sede.nome}`,
                                  })),
                                )
                              })
                              .catch((error) => {
                                console.log('error', error)
                              })
                        },
                      }}
                    >
                      <RequiredRule message="Azienda destinazione obbligatoria" />
                    </SimpleItem>
                  </GroupItem>
                </DXForm>
                {migrationData.azienda_destinazione && (
                  <>
                    {sediDaMigrareList.length > 0 ? (
                      <ResponsiveBox singleColumnScreen="xs sm" className="migration-cards-layout">
                        {rows}
                        {cols}
                        {sediDaMigrareList.map((sede, index) => (
                          <ResponsiveItem key={sede.id}>
                            <Location row={Math.floor(index / 2)} col={index % 2} />
                            <div className={`migration-card ${!sede.selected ? 'migration-card-disabled' : ''}`}>
                              <div className="migration-card-header">
                                <CheckBox
                                  value={sede.selected}
                                  onValueChanged={(e) => updateSede(index, { selected: e.value ?? false })}
                                />
                                <span className="migration-card-title">{sede.nome}</span>
                              </div>
                              <div className="migration-card-body">
                                <div className="migration-field">
                                  <span className="migration-field-label">Azione</span>
                                  <SelectBox
                                    dataSource={azioniMigrazione}
                                    displayExpr="text"
                                    valueExpr="id"
                                    value={sede.azione}
                                    disabled={!sede.selected}
                                    onValueChanged={(e) => {
                                      const newAzione = e.value as AzioneMigrazione
                                      if (newAzione === 'nuova') {
                                        updateSede(index, {
                                          azione: 'nuova',
                                          sede_destinazione: undefined,
                                          nuovo_nome: !sediDestinazioneList.find((s) => s.nome === sede.nome)
                                            ? `${sede.nome}`
                                            : sede.nome + ' 1',
                                        })
                                      } else {
                                        updateSede(index, {
                                          azione: 'unifica',
                                          nuovo_nome: '',
                                        })
                                      }
                                    }}
                                  />
                                </div>
                                <div className="migration-field">
                                  <label className="migration-field-label">
                                    {sede.azione === 'nuova'
                                      ? 'Nuovo nome sede di destinazione'
                                      : 'Sede di destinazione esistente'}
                                  </label>
                                  {sede.azione === 'nuova' ? (
                                    <TextBox
                                      value={sede.nuovo_nome}
                                      placeholder="Inserisci nuovo nome sede..."
                                      disabled={!sede.selected}
                                      onValueChanged={(e) => updateSede(index, { nuovo_nome: e.value ?? '' })}
                                    />
                                  ) : (
                                    <SelectBox
                                      dataSource={sediDestinazioneList}
                                      displayExpr="nome"
                                      valueExpr="id"
                                      value={sede.sede_destinazione?.id ?? null}
                                      showClearButton={true}
                                      searchEnabled={true}
                                      disabled={!sede.selected}
                                      placeholder="Seleziona sede destinazione..."
                                      onValueChanged={(e) => {
                                        const sedeFound = sediDestinazioneList.find((s) => s.id === e.value)
                                        updateSede(index, { sede_destinazione: sedeFound ?? undefined })
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </ResponsiveItem>
                        ))}
                      </ResponsiveBox>
                    ) : (
                      <div className="migration-no-data">Nessuna sede da migrare</div>
                    )}
                  </>
                )}

                <Toolbar className="last-group">
                  <ToolbarItem location="after" locateInMenu="never">
                    <Button
                      disabled={!migrationData.azienda_destinazione}
                      text="Migra"
                      icon="import"
                      useSubmitBehavior={false}
                      width={'150px'}
                      onClick={async (e: ClickEvent) => {
                        const validationResult = formRef.current?.instance().validate()
                        if (!validationResult?.isValid) return

                        const selectedSites = sediDaMigrareList.filter((sede) => sede.selected)
                        if (selectedSites.length === 0) {
                          notify(`Selezionare almeno una sede.`, 'warning', 2000)
                          return
                        }
                        let hasInvalidRow = false
                        const errors: string[] = []
                        for (const data of selectedSites) {
                          if (data.azione === 'nuova') {
                            if (!data.nuovo_nome || data.nuovo_nome.trim() === '') {
                              hasInvalidRow = true
                              errors.push(`Sede "${data.nome}": nuovo nome obbligatorio`)
                            }
                          } else if (data.azione === 'unifica') {
                            if (!data.sede_destinazione?.id) {
                              hasInvalidRow = true
                              errors.push(`Sede "${data.nome}": sede di destinazione obbligatoria`)
                            } else if (data.sede_destinazione.id === data.id) {
                              hasInvalidRow = true
                              errors.push(`Sede "${data.nome}": sede di destinazione deve essere diversa`)
                            }
                          }
                        }
                        if (hasInvalidRow) {
                          notify(errors[0] || 'Errori nelle righe selezionate.', 'error', 3000)
                          return
                        }
                        const result = confirm(
                          `<i>Vuoi davvero effetture l'operazione di <b>migrazione</b> del cliente <b>${migrationData.azienda_da_migrare?.nome}</b> al cliente <b>${migrationData.azienda_destinazione?.nome}</b>?</i>`,
                          'Migra',
                        )
                        result.then(async (dialogResult) => {
                          if (dialogResult === false) return
                          setLoadPanleVisible(true)
                          try {
                            const response: AxiosResponse<any, any> = await client.put(
                              '/api/azienda/migrate',
                              {
                                azienda_da_migrare: migrationData.azienda_da_migrare,
                                azienda_destinazione: migrationData.azienda_destinazione,
                                nuove_sedi: selectedSites,
                              },
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                              },
                            )
                            navigate(`/clients/${migrationData.azienda_destinazione?.id}/sites`)
                          } catch (error) {
                            notify(`Errore migrazione sedi. Errore:  ${error}`, 'error', 2000)
                            setLoadPanleVisible(false)
                          }
                        })
                      }}
                    />
                  </ToolbarItem>
                </Toolbar>
              </div>
            </>
          )}
        </Await>
      </Suspense>
    </>
  )
}

export default ClientMigration
